// Import full Bootstrap bundle (includes Popper.js)
import * as bootstrap from "bootstrap";

let copyToClipboardInitialized = false;

function ensureToastContainer() {
  let container = document.querySelector("[data-toast-container='global']");
  if (container) {
    return container;
  }

  container = document.createElement("div");
  container.className = "toast-container position-fixed top-0 end-0 p-3";
  container.dataset.toastContainer = "global";
  document.body.appendChild(container);

  return container;
}

const TOAST_VARIANTS = {
  info: "border-info-subtle text-bg-light",
  success: "border-success-subtle text-bg-success",
  danger: "border-danger-subtle text-bg-danger",
  warning: "border-warning-subtle text-bg-warning",
};

function findToastById(container, toastId) {
  if (!toastId) {
    return null;
  }

  return (
    Array.from(container.querySelectorAll("[data-toast-id]")).find((toast) => toast.dataset.toastId === toastId) || null
  );
}

function showToast(message, options = {}) {
  const {
    type = "info",
    delay = 5200,
    autohide = true,
    toastId = "",
    showCloseButton = true,
    showSpinner = false,
  } = options;
  const container = ensureToastContainer();
  const variantClass = TOAST_VARIANTS[type] || TOAST_VARIANTS.info;
  const existingToast = findToastById(container, toastId);
  const toast = existingToast || document.createElement("div");

  toast.className = `toast align-items-center ${variantClass}`;
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");
  toast.setAttribute("aria-atomic", "true");

  if (toastId) {
    toast.dataset.toastId = toastId;
  }

  const spinnerHtml = showSpinner
    ? '<span class="spinner-border spinner-border-sm me-2 flex-shrink-0" role="status" aria-hidden="true"></span>'
    : "";
  const closeButtonHtml = showCloseButton
    ? '<button type="button" class="btn-close me-2 m-auto" data-bs-dismiss="toast" aria-label="Zamknij"></button>'
    : "";

  toast.innerHTML = `
      <div class="d-flex align-items-center"><div class="toast-body">${escapeHtml(message)} ${spinnerHtml}</div>
      ${closeButtonHtml}</div>
  `;

  if (!existingToast) {
    container.appendChild(toast);
  }

  const instance = bootstrap.Toast.getOrCreateInstance(toast, {
    autohide,
    delay,
  });

  if (!toast.dataset.toastRemoveHandlerAttached) {
    toast.addEventListener("hidden.bs.toast", () => {
      toast.remove();
    });
    toast.dataset.toastRemoveHandlerAttached = "1";
  }

  instance.show();

  return toast;
}

function hideToast(toastId) {
  if (!toastId) {
    return;
  }

  const container = ensureToastContainer();
  const toast = findToastById(container, toastId);
  if (!toast) {
    return;
  }

  const instance = bootstrap.Toast.getInstance(toast) || bootstrap.Toast.getOrCreateInstance(toast);
  instance.hide();
}

function showClipboardToast(message) {
  showToast(message, { type: "info" });
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

async function writeToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "readonly");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  const success = document.execCommand("copy");
  textarea.remove();

  if (!success) {
    throw new Error("Clipboard copy failed");
  }
}

function initCopyToClipboard() {
  if (copyToClipboardInitialized) {
    return;
  }

  copyToClipboardInitialized = true;

  document.addEventListener("click", async (event) => {
    const trigger = event.target instanceof Element ? event.target.closest("[data-copy-to-clipboard='1']") : null;

    if (!trigger) {
      return;
    }

    event.preventDefault();

    const copyValue = String(trigger.getAttribute("data-copy-value") ?? "").trim();
    if (!copyValue) {
      return;
    }

    const originalDisabled = trigger.getAttribute("aria-disabled");
    trigger.setAttribute("aria-disabled", "true");

    try {
      await writeToClipboard(copyValue);
      showClipboardToast(trigger.getAttribute("data-copy-success-message") || "Skopiowano do schowka");
    } catch {
      showClipboardToast("Nie udało się skopiować wartości");
    } finally {
      if (originalDisabled === null) {
        trigger.removeAttribute("aria-disabled");
      } else {
        trigger.setAttribute("aria-disabled", originalDisabled);
      }
    }
  });
}

export function BS5Init() {
  initCopyToClipboard();

  // Initialize all Bootstrap tooltips
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  [...tooltipTriggerList].forEach((tooltipTriggerEl) => {
    bootstrap.Tooltip.getOrCreateInstance(tooltipTriggerEl);
  });

  // Initialize all Bootstrap popovers
  const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
  [...popoverTriggerList].forEach((popoverTriggerEl) => {
    new bootstrap.Popover(popoverTriggerEl);
  });

  // Initialize all Bootstrap dropdowns
  const dropdownElementList = document.querySelectorAll("[data-bs-toggle='dropdown']");
  [...dropdownElementList].forEach((dropdownToggleEl) => {
    new bootstrap.Dropdown(dropdownToggleEl);
  });

  // Initialize all Bootstrap modals on the modal elements, not their triggers.
  const modalElementList = document.querySelectorAll(".modal");
  [...modalElementList].forEach((modalEl) => {
    bootstrap.Modal.getOrCreateInstance(modalEl);
  });

  // Initialize all Bootstrap alerts with close button
  const alertElementList = document.querySelectorAll("[data-bs-dismiss='alert']");
  [...alertElementList].forEach((alertEl) => {
    new bootstrap.Alert(alertEl);
  });

  // Initialize panels without toggling their initial state or modifying trigger buttons.
  const collapseElementList = document.querySelectorAll(".collapse");
  [...collapseElementList].forEach((collapseEl) => {
    bootstrap.Collapse.getOrCreateInstance(collapseEl, { toggle: false });
  });

  // Initialize all Bootstrap tabs
  const tabElementList = document.querySelectorAll("[data-bs-toggle='tab'], [data-bs-toggle='pill']");
  [...tabElementList].forEach((tabEl) => {
    new bootstrap.Tab(tabEl);
    tabEl.addEventListener("shown.bs.tab", function (event) {
      const target = event.target.getAttribute("data-bs-target");
      history.replaceState(null, null, target);
    });
  });

  const hash = window.location.hash;
  if (hash) {
    const triggerEl = document.querySelector(`[data-bs-target="${hash}"]`);
    if (triggerEl) {
      const tab = new bootstrap.Tab(triggerEl);
      tab.show();
    }
  }

  // Initialize all Bootstrap scrollspy
  const scrollspyElementList = document.querySelectorAll("[data-bs-spy='scroll']");
  [...scrollspyElementList].forEach((scrollspyEl) => {
    bootstrap.ScrollSpy.getOrCreateInstance(scrollspyEl).refresh();
  });
}

export { showToast, hideToast, showClipboardToast, escapeHtml };

export class BS5Validator {
  constructor(root = document) {
    this.root = root;
    this.validators = {
      regon: isValidREGON,
      nip: isValidNIP,
      phone: isValidPhone,
      postalCode: isValidPostalCode,
    };

    this.init();
  }

  init() {
    this.root.querySelectorAll("[data-validate]").forEach((input) => this.attach(input));
  }

  attach(input) {
    if (input.dataset.validatorAttached) return;
    input.dataset.validatorAttached = "1";

    this.ensureFeedback(input);

    const spec = this.parseValidateSpec(input.dataset.validate);
    this.ensureCounter(input, spec);
    this.updateCounter(input, input.value ?? "", spec);

    if (typeof spec.maxField === "string" && spec.maxField !== "") {
      const form = input.closest("form");
      const comparedInput = form?.querySelector(`[name="${CSS.escape(spec.maxField)}"]`);
      if (comparedInput) {
        comparedInput.addEventListener("input", () => this.validate(input));
        comparedInput.addEventListener("change", () => this.validate(input));
      }
    }

    input.addEventListener("input", () => {
      this.validate(input);
    });

    input.addEventListener("blur", () => {
      this.validate(input);
    });
  }

  validate(input) {
    const spec = this.parseValidateSpec(input.dataset.validate);
    const value = (input.value ?? "").trim();

    this.ensureCounter(input, spec);
    this.updateCounter(input, input.value ?? "", spec);

    const checks = this.runValidationChecks(value, spec, input);

    // Keep old behavior: don't show validation state for empty inputs.
    if (value === "") {
      input.setCustomValidity("");
      this.reset(input);
      return;
    }

    input.setCustomValidity(
      checks.isValid ? "" : checks.message || input.dataset.invalidMessage || "Nieprawidłowa wartość",
    );

    input.classList.toggle("is-valid", checks.isValid);
    input.classList.toggle("is-invalid", !checks.isValid);

    if (!checks.isValid) {
      const feedback = this.getOrCreateFeedback(input);
      feedback.textContent = checks.message || input.dataset.invalidMessage || "Nieprawidłowa wartość";
    }
  }

  reset(input) {
    input.classList.remove("is-valid", "is-invalid");
  }

  isFeedbackElement(element) {
    return Boolean(element?.classList?.contains("invalid-feedback"));
  }

  getFeedbackFieldId(input) {
    const fieldId = String(input?.id ?? "").trim();
    return fieldId !== "" ? fieldId : null;
  }

  matchesFeedbackForInput(feedback, input) {
    if (!this.isFeedbackElement(feedback)) {
      return false;
    }

    const expectedFieldId = this.getFeedbackFieldId(input);
    const feedbackFieldId = String(feedback.dataset.validationFeedbackFor ?? "").trim();

    if (expectedFieldId && feedbackFieldId) {
      return feedbackFieldId === expectedFieldId;
    }

    return !feedbackFieldId;
  }

  findExistingFeedback(input) {
    const directSibling = input.nextElementSibling;
    if (this.matchesFeedbackForInput(directSibling, input)) {
      return directSibling;
    }

    const formAfter = this.getFormAfterContainer(input);
    const feedbackAfterHelper = formAfter?.nextElementSibling;
    if (this.matchesFeedbackForInput(feedbackAfterHelper, input)) {
      return feedbackAfterHelper;
    }

    const fieldId = this.getFeedbackFieldId(input);
    const fieldRow = input.closest("[data-field-row]") ?? input.closest(".form__row") ?? input.parentElement;
    if (!fieldRow) {
      return null;
    }

    if (fieldId) {
      const markedFeedback = fieldRow.querySelector(
        `.invalid-feedback[data-validation-feedback-for="${CSS.escape(fieldId)}"]`,
      );
      if (this.isFeedbackElement(markedFeedback)) {
        return markedFeedback;
      }
    }

    const feedbackCandidates = [...fieldRow.querySelectorAll(".invalid-feedback")];
    if (feedbackCandidates.length === 1) {
      return feedbackCandidates[0];
    }

    return feedbackCandidates.find((feedback) => this.matchesFeedbackForInput(feedback, input)) ?? null;
  }

  ensureFeedback(input) {
    if (this.findExistingFeedback(input)) return;

    const feedback = input.ownerDocument.createElement("div");
    feedback.className = "invalid-feedback";
    feedback.textContent = input.dataset.invalidMessage || "Nieprawidłowa wartość";

    const fieldId = this.getFeedbackFieldId(input);
    if (fieldId) {
      feedback.dataset.validationFeedbackFor = fieldId;
    }

    input.insertAdjacentElement("afterend", feedback);
  }

  getOrCreateFeedback(input) {
    this.ensureFeedback(input);
    return this.findExistingFeedback(input);
  }

  getFormAfterContainer(input) {
    return input.closest(".col-sm-8")?.querySelector?.(".form-after") ?? null;
  }

  ensureCounter(input, spec) {
    const rules = spec && typeof spec === "object" ? spec : {};
    if (!(typeof rules.maxLength === "number" && Number.isFinite(rules.maxLength))) {
      return;
    }

    const max = Math.max(0, Math.floor(rules.maxLength));
    const existing = this.getCounterElement(input);
    if (existing) {
      existing.dataset.maxLength = String(max);
      return;
    }

    // Keep invalid-feedback as the immediate sibling (bootstrap compatibility)
    this.ensureFeedback(input);

    const counter = document.createElement("div");
    counter.className = "form-text text-end text-muted";
    counter.dataset.validateCounter = "1";
    counter.dataset.maxLength = String(max);

    const formAfter = this.getFormAfterContainer(input);
    if (formAfter) {
      formAfter.appendChild(counter);
      return;
    }

    const feedback = this.getOrCreateFeedback(input);
    feedback.insertAdjacentElement("afterend", counter);
  }

  getCounterElement(input) {
    const formAfter = this.getFormAfterContainer(input);
    const nested = formAfter?.querySelector?.("[data-validate-counter='1']");
    if (nested) return nested;

    // Legacy fallback: counter placed right after invalid-feedback.
    const feedback = this.findExistingFeedback(input);
    const candidate = feedback?.nextElementSibling;
    if (candidate?.dataset?.validateCounter === "1") return candidate;

    // Fallback if DOM got rearranged.
    return input.parentElement?.querySelector?.("[data-validate-counter='1']") ?? null;
  }

  updateCounter(input, rawValue, spec) {
    const rules = spec && typeof spec === "object" ? spec : {};
    if (!(typeof rules.maxLength === "number" && Number.isFinite(rules.maxLength))) {
      return;
    }

    const max = Math.max(0, Math.floor(rules.maxLength));
    const counter = this.getCounterElement(input);
    if (!counter) return;

    const length = String(rawValue ?? "").length;
    counter.textContent = `${length} / ${max}`;
    counter.classList.toggle("text-danger", length > max);
    counter.classList.toggle("text-muted", length <= max);
  }

  parseValidateSpec(raw) {
    const trimmed = String(raw ?? "").trim();

    // Backwards-compatible: data-validate="regon"
    if (!trimmed) return {};
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
      return { type: trimmed };
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      // ignore and fallback
    }

    return { type: trimmed };
  }

  runValidationChecks(value, spec, input = null) {
    const rules = spec && typeof spec === "object" ? spec : {};

    // 1) maxLength
    if (typeof rules.maxLength === "number" && Number.isFinite(rules.maxLength)) {
      const max = Math.max(0, Math.floor(rules.maxLength));
      if (value.length > max) {
        return { isValid: false, message: `Maksymalnie ${max} znaków` };
      }
    }

    // 2) value cannot exceed another numeric field in the same form
    if (typeof rules.maxField === "string" && rules.maxField !== "" && input) {
      const form = input.closest("form");
      const comparedInput = form?.querySelector(`[name="${CSS.escape(rules.maxField)}"]`);
      const comparedValue = String(comparedInput?.value ?? "").trim();

      if (comparedValue !== "" && Number.isFinite(Number(value)) && Number.isFinite(Number(comparedValue))) {
        if (Number(value) > Number(comparedValue)) {
          const comparedLabel = String(rules.maxFieldLabel ?? rules.maxField);
          return { isValid: false, message: `Wartość nie może być większa niż „${comparedLabel}”.` };
        }
      }
    }

    // 3) named validator (legacy or JSON)
    const type = rules.type || rules.validator;
    if (typeof type === "string" && type) {
      const validator = this.validators[type];
      if (typeof validator === "function") {
        const result = validator(value);

        if (typeof result === "boolean") {
          return { isValid: result, message: null };
        }

        if (result && typeof result === "object") {
          return {
            isValid: Boolean(result.isValid),
            message: typeof result.message === "string" ? result.message : null,
          };
        }

        return { isValid: false, message: null };
      }
    }

    // If there are no rules we recognize, treat as valid (no-op).
    return { isValid: true, message: null };
  }
}

function isValidREGON(regon) {
  if (!/^\d{9}(\d{5})?$/.test(regon)) {
    return { isValid: false, message: null };
  }

  const digits = regon.split("").map(Number);

  const weights9 = [8, 9, 2, 3, 4, 5, 6, 7];
  const weights14 = [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8];

  if (digits.length === 9) {
    const sum = weights9.reduce((acc, w, i) => acc + w * digits[i], 0);
    const control = sum % 11 === 10 ? 0 : sum % 11;
    return {
      isValid: control === digits[8],
      message: control === digits[8] ? null : "REGON ma poprawną długość, ale nie przechodzi sumy kontrolnej.",
    };
  }

  if (digits.length === 14) {
    const sum = weights14.reduce((acc, w, i) => acc + w * digits[i], 0);
    const control = sum % 11 === 10 ? 0 : sum % 11;
    return {
      isValid: control === digits[13],
      message: control === digits[13] ? null : "REGON ma poprawną długość, ale nie przechodzi sumy kontrolnej.",
    };
  }

  return { isValid: false, message: null };
}

function isValidNIP(nip) {
  const digits = String(nip ?? "").trim();
  if (!/^\d{10}$/.test(digits)) {
    return { isValid: false, message: null };
  }

  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  const sum = weights.reduce((acc, weight, index) => acc + weight * Number(digits[index]), 0);
  const control = sum % 11;

  if (control === 10) {
    return { isValid: false, message: "NIP ma poprawną długość, ale nie przechodzi sumy kontrolnej." };
  }

  return {
    isValid: control === Number(digits[9]),
    message: control === Number(digits[9]) ? null : "NIP ma poprawną długość, ale nie przechodzi sumy kontrolnej.",
  };
}

function isValidPostalCode(postalCode) {
  return /^\d{2}-\d{3}$/.test(String(postalCode ?? "").trim());
}

function isValidPhone(phone) {
  const value = String(phone ?? "").trim();
  return /^(?=.*\d)\+?[\d -]+$/.test(value);
}

export default {
  BS5Init,
  BS5Validator,
};
